import Button from '@superfunnel/ui-core/v2/Button';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { classNames } from '@superfunnel/utils';
import { ReactNode } from 'react';

export interface AttachmentChipProps {
  icon: ReactNode;
  label: string;
  tooltip?: string;
  size?: 'sm' | 'lg';

  // Interactive mode props
  onDelete?: () => void;
  isUploading?: boolean;
  progress?: number;
  hasError?: boolean;
}

/**
 * Renders a file type icon based on mimeType, or an image thumbnail if src is provided.
 */
export const AttachmentIcon = ({
  mimeType,
  src,
  name,
}: {
  mimeType?: string;
  src?: string;
  name?: string;
}) => {
  // Show image thumbnail if src is provided and it's an image
  if (src && mimeType?.startsWith('image/')) {
    return (
      <img
        src={src}
        alt={name || 'image'}
        className="size-7.5 rounded-lg object-cover"
      />
    );
  }

  // PDF icon
  if (mimeType === 'application/pdf' || mimeType?.endsWith('/pdf')) {
    return (
      <svg
        width="26"
        height="28"
        viewBox="0 0 26 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.25 25.8462H19.75C20.9926 25.8462 22 24.7854 22 23.4769V9.76923L14.6364 2.15385H6.25C5.00736 2.15385 4 3.21459 4 4.52308V23.4769C4 24.7854 5.00736 25.8462 6.25 25.8462Z"
          fill="#795DFC"
        />
        <path
          d="M7.00107 20.6364V14.6364H8.01243V15.358H8.07209C8.12512 15.2519 8.19993 15.1392 8.29652 15.0199C8.39311 14.8987 8.52379 14.7955 8.68857 14.7102C8.85334 14.6231 9.06357 14.5795 9.31925 14.5795C9.65637 14.5795 9.96035 14.6657 10.2312 14.8381C10.5039 15.0085 10.7198 15.2614 10.8789 15.5966C11.0399 15.9299 11.1204 16.339 11.1204 16.8239C11.1204 17.303 11.0418 17.7102 10.8846 18.0455C10.7274 18.3807 10.5134 18.6364 10.2425 18.8125C9.97171 18.9886 9.66489 19.0767 9.32209 19.0767C9.07209 19.0767 8.8647 19.035 8.69993 18.9517C8.53516 18.8684 8.40258 18.768 8.3022 18.6506C8.20372 18.5312 8.12701 18.4186 8.07209 18.3125H8.02947V20.6364H7.00107ZM8.00959 16.8182C8.00959 17.1004 8.04936 17.3475 8.12891 17.5597C8.21035 17.7718 8.32682 17.9375 8.47834 18.0568C8.63175 18.1742 8.81735 18.233 9.03516 18.233C9.26243 18.233 9.45277 18.1723 9.60618 18.0511C9.75959 17.928 9.87512 17.7604 9.95277 17.5483C10.0323 17.3343 10.0721 17.0909 10.0721 16.8182C10.0721 16.5473 10.0333 16.3068 9.95561 16.0966C9.87796 15.8864 9.76243 15.7216 9.60902 15.6023C9.45561 15.483 9.26432 15.4233 9.03516 15.4233C8.81546 15.4233 8.62891 15.4811 8.4755 15.5966C8.32209 15.7121 8.20561 15.8741 8.12607 16.0824C8.04841 16.2907 8.00959 16.536 8.00959 16.8182ZM13.6204 19.0767C13.2776 19.0767 12.9708 18.9886 12.6999 18.8125C12.4291 18.6364 12.2151 18.3807 12.0579 18.0455C11.9007 17.7102 11.8221 17.303 11.8221 16.8239C11.8221 16.339 11.9016 15.9299 12.0607 15.5966C12.2217 15.2614 12.4386 15.0085 12.7113 14.8381C12.984 14.6657 13.288 14.5795 13.6232 14.5795C13.8789 14.5795 14.0891 14.6231 14.2539 14.7102C14.4187 14.7955 14.5494 14.8987 14.646 15.0199C14.7425 15.1392 14.8174 15.2519 14.8704 15.358H14.913V13.1818H15.9442V19H14.9329V18.3125H14.8704C14.8174 18.4186 14.7406 18.5312 14.6403 18.6506C14.5399 18.768 14.4073 18.8684 14.2425 18.9517C14.0778 19.035 13.8704 19.0767 13.6204 19.0767ZM13.9073 18.233C14.1251 18.233 14.3107 18.1742 14.4641 18.0568C14.6175 17.9375 14.734 17.7718 14.8136 17.5597C14.8931 17.3475 14.9329 17.1004 14.9329 16.8182C14.9329 16.536 14.8931 16.2907 14.8136 16.0824C14.7359 15.8741 14.6204 15.7121 14.467 15.5966C14.3155 15.4811 14.1289 15.4233 13.9073 15.4233C13.6781 15.4233 13.4869 15.483 13.3335 15.6023C13.18 15.7216 13.0645 15.8864 12.9869 16.0966C12.9092 16.3068 12.8704 16.5473 12.8704 16.8182C12.8704 17.0909 12.9092 17.3343 12.9869 17.5483C13.0664 17.7604 13.1829 17.928 13.3363 18.0511C13.4916 18.1723 13.6819 18.233 13.9073 18.233ZM19.2504 14.6364V15.4318H16.6708V14.6364H19.2504ZM17.3157 19V14.2244C17.3157 13.9309 17.3763 13.6866 17.4975 13.4915C17.6206 13.2964 17.7854 13.1506 17.9918 13.054C18.1983 12.9574 18.4274 12.9091 18.6793 12.9091C18.8574 12.9091 19.0155 12.9233 19.1538 12.9517C19.292 12.9801 19.3943 13.0057 19.4606 13.0284L19.256 13.8239C19.2125 13.8106 19.1576 13.7973 19.0913 13.7841C19.025 13.7689 18.9511 13.7614 18.8697 13.7614C18.6784 13.7614 18.543 13.8078 18.4634 13.9006C18.3858 13.9915 18.3469 14.1222 18.3469 14.2926V19H17.3157Z"
          fill="white"
        />
        <path
          d="M14.6364 6.84024V2.15385L22 9.76923H17.4685C15.9044 9.76923 14.6364 8.45788 14.6364 6.84024Z"
          fill="#B7A1FF"
        />
      </svg>
    );
  }

  // Default file icon
  return (
    <svg
      width="26"
      height="28"
      viewBox="0 0 26 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.25 25.8462H19.75C20.9926 25.8462 22 24.7854 22 23.4769V9.76923L14.6364 2.15385H6.25C5.00736 2.15385 4 3.21459 4 4.52308V23.4769C4 24.7854 5.00736 25.8462 6.25 25.8462Z"
        fill="#795DFC"
      />
      <path
        d="M14.6364 6.84024V2.15385L22 9.76923H17.4685C15.9044 9.76923 14.6364 8.45788 14.6364 6.84024Z"
        fill="#B7A1FF"
      />
    </svg>
  );
};

/**
 * Shared attachment chip component for displaying file, campaign, or element attachments.
 * Supports both interactive mode (with delete button, progress) and display-only mode.
 */
export const AttachmentChip = ({
  icon,
  label,
  tooltip,
  size = 'sm',
  onDelete,
  isUploading = false,
  progress = 0,
  hasError = false,
}: AttachmentChipProps) => {
  const isInteractive = !!onDelete;

  return (
    <div
      className={classNames(
        'rounded-2lg font-inter relative flex h-8.5 shrink-0 items-center gap-2 overflow-hidden border p-0.5 text-sm',
        // Normal state
        !isUploading &&
          !hasError &&
          'border-base-200 hover:border-base-300 hover:bg-base-50',
        // Uploading state with animation
        isUploading &&
          'border-base-300 bg-base-50 hover:border-base-300 hover:bg-base-50 animate-pulse border-dashed',
        // Error state
        hasError &&
          'border-red-300 bg-red-50 hover:border-red-400 hover:bg-red-100',
        // Interactive mode gets cursor pointer and z-index
        isInteractive && 'cursor-pointer',
      )}
      onClick={(e) => {
        if (isInteractive) {
          e.stopPropagation();
        }
      }}
      title={!isInteractive ? tooltip : undefined}
    >
      {/* Progress bar background */}
      {isUploading && (
        <div
          className="bg-base-100 absolute inset-0 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      )}

      <div
        className={classNames(
          'relative z-10 flex items-center gap-1',
          !isInteractive && 'pe-2',
        )}
      >
        {icon}
        <div
          className={classNames(
            'truncate text-ellipsis',
            size === 'sm' ? 'max-w-28' : 'max-w-54',
          )}
        >
          {label}
        </div>
      </div>

      {/* Progress percentage */}
      {isUploading && (
        <div className="text-base-500 relative z-10 mr-1 text-xs">
          {Math.round(progress)}%
        </div>
      )}

      {/* Error indicator */}
      {hasError && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="relative z-10 mr-1 text-xs text-red-500"
              title="Upload failed"
            >
              ⚠️
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>Failed to upload, remove and try again</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Delete button - only shown in interactive mode */}
      {isInteractive && (
        <div className="relative z-10 flex items-center">
          <Separator orientation="vertical" className="h-5" />
          <Button size="icon" variant="ghost" onClick={onDelete}>
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.11089 14.8891L11 11M14.8891 7.11091L11 11M11 11L7.11089 7.11091M11 11L14.8891 14.8891"
                stroke="#141C25"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>
      )}
    </div>
  );
};
